import { useEffect, useMemo, useState } from 'react'
import AdminShell from './AdminShell'
import {
  AdminButton,
  AdminCard,
  AdminCardHeader,
  AdminDataTable,
  AdminField,
  AdminInput,
  AdminModal,
  AdminSkeleton,
  AdminStatePanel,
  AdminStatusPill,
} from './AdminUi'
import '../styles/admin-security.css'

type SessionPayload = {
  authenticated: true
  user: { email: string; role: string }
  csrfToken: string
}

type SecuritySession = {
  id: string
  current: boolean
  createdAt: string
  lastSeenAt: string
  idleExpiresAt: string
  absoluteExpiresAt: string
}

type SecurityUser = {
  email: string
  role: string
  status: string
  mfaEnrolled: boolean
  lastLoginAt: string | null
}

type SecurityActivity = {
  event: string
  label: string
  outcome: string
  actor: string
  createdAt: string
}

type SecurityPayload = {
  account: {
    email: string
    role: string
    status: string
    createdAt: string
    lastLoginAt: string | null
    mfa: { enrolled: boolean; enrolledAt: string | null }
    recoveryCodesRemaining: number
  }
  policy: {
    ownerOnly: boolean
    staffAccessEnabled: boolean
    mfaRequired: boolean
    passwordMinLength: number
    passwordMaxLength: number
    forcedComplexityClasses: boolean
    sessionIdleHours: number
    sessionAbsoluteHours: number
    cookieSecurity: string[]
    csrf: string
    stepUp: string
    stepUpFailureLimit: number
    stepUpWindowMinutes: number
  }
  sessions: SecuritySession[]
  users: SecurityUser[]
  activity: SecurityActivity[]
}

type SecurityEnvelope = {
  security?: SecurityPayload
  code?: string
  message?: string
  revoked?: boolean
  alreadyInactive?: boolean
  revokedCount?: number
  changed?: boolean
  regenerated?: boolean
  recoveryCodes?: string[]
}

type SecretForm = {
  currentPassword: string
  currentTotp: string
  newPassword: string
  confirmPassword: string
}

const emptySecretForm: SecretForm = {
  currentPassword: '',
  currentTotp: '',
  newPassword: '',
  confirmPassword: '',
}

const DATE_TIME = new Intl.DateTimeFormat('en-NZ', {
  timeZone: 'Pacific/Auckland',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
})

function when(value: string | null | undefined) {
  if (!value) return 'Not recorded'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Not recorded' : DATE_TIME.format(date)
}

function environmentFromHostname(hostname: string) {
  const host = hostname.toLowerCase()
  if (host.includes('staging')) return 'Staging'
  if (host === 'localhost' || host === '127.0.0.1') return 'Local'
  return undefined
}

function outcomeTone(outcome: string): 'success' | 'warning' | 'danger' | 'neutral' {
  if (outcome === 'success') return 'success'
  if (outcome === 'failure') return 'danger'
  if (outcome === 'denied') return 'warning'
  return 'neutral'
}

export default function AdminSecurityPage() {
  const [session, setSession] = useState<SessionPayload | null>(null)
  const [security, setSecurity] = useState<SecurityPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState('')
  const [logoutBusy, setLogoutBusy] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<SecuritySession | null>(null)
  const [revokeOthersOpen, setRevokeOthersOpen] = useState(false)
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [recoveryOpen, setRecoveryOpen] = useState(false)
  const [secretForm, setSecretForm] = useState<SecretForm>(emptySecretForm)
  const [issuedRecoveryCodes, setIssuedRecoveryCodes] = useState<string[]>([])
  const environmentLabel = useMemo(() => environmentFromHostname(window.location.hostname), [])

  async function load() {
    setLoading(true)
    setError('')
    try {
      const [sessionResponse, securityResponse] = await Promise.all([
        fetch('/api/admin/session', { credentials: 'same-origin', cache: 'no-store' }),
        fetch('/api/admin/security', { credentials: 'same-origin', cache: 'no-store' }),
      ])
      if (sessionResponse.status === 401 || securityResponse.status === 401) {
        window.location.replace('/admin/login?next=/admin/security')
        return
      }
      const sessionPayload = await sessionResponse.json().catch(() => null) as SessionPayload | null
      const envelope = await securityResponse.json().catch(() => null) as SecurityEnvelope | null
      if (!sessionResponse.ok || !sessionPayload?.authenticated) throw new Error('Boss Zone session could not be loaded.')
      if (!securityResponse.ok || !envelope?.security) throw new Error(envelope?.message || 'Admin & Security could not be loaded.')
      setSession(sessionPayload)
      setSecurity(envelope.security)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Admin & Security could not be loaded.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  async function post(path: string, body: Record<string, unknown>) {
    if (!session) throw new Error('Session unavailable.')
    const response = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': session.csrfToken,
      },
      body: JSON.stringify(body),
    })
    const envelope = await response.json().catch(() => null) as SecurityEnvelope | null
    if (response.status === 401 && envelope?.code === 'unauthenticated') {
      window.location.replace('/admin/login?next=/admin/security')
      throw new Error('Authentication required.')
    }
    if (!response.ok) throw new Error(envelope?.message || 'Security action could not be completed.')
    return envelope ?? {}
  }

  async function revokeSession() {
    if (!revokeTarget || busy) return
    setBusy('revoke-session')
    setError('')
    try {
      await post(`/api/admin/security/sessions/${encodeURIComponent(revokeTarget.id)}/revoke`, {})
      setRevokeTarget(null)
      setNotice('The other session has been revoked.')
      await load()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The session could not be revoked.')
    } finally {
      setBusy('')
    }
  }

  async function revokeOthers() {
    if (busy) return
    setBusy('revoke-others')
    setError('')
    try {
      const result = await post('/api/admin/security/sessions/revoke-others', {})
      setRevokeOthersOpen(false)
      setNotice(`${result.revokedCount ?? 0} other session${result.revokedCount === 1 ? '' : 's'} revoked. This session remains active.`)
      await load()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Other sessions could not be revoked.')
    } finally {
      setBusy('')
    }
  }

  function updateSecretField(field: keyof SecretForm, value: string) {
    setSecretForm((current) => ({ ...current, [field]: value }))
    setError('')
  }

  function closePassword() {
    if (busy) return
    setPasswordOpen(false)
    setSecretForm(emptySecretForm)
  }

  async function changePassword() {
    if (!security || busy) return
    if (secretForm.newPassword !== secretForm.confirmPassword) {
      setError('The new password confirmation does not match.')
      return
    }
    if (secretForm.newPassword.length < security.policy.passwordMinLength || secretForm.newPassword.length > security.policy.passwordMaxLength) {
      setError(`Use a new password between ${security.policy.passwordMinLength} and ${security.policy.passwordMaxLength} characters.`)
      return
    }
    if (!/^\d{6}$/.test(secretForm.currentTotp.trim())) {
      setError('Enter the current 6-digit authenticator code.')
      return
    }
    setBusy('password')
    setError('')
    try {
      await post('/api/admin/security/password', {
        currentPassword: secretForm.currentPassword,
        currentTotp: secretForm.currentTotp.trim(),
        newPassword: secretForm.newPassword,
      })
      setPasswordOpen(false)
      setSecretForm(emptySecretForm)
      setNotice('Password changed. Other Boss Zone sessions were revoked; this verified session remains active.')
      await load()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Password could not be changed.')
    } finally {
      setBusy('')
    }
  }

  function closeRecovery() {
    if (busy) return
    setRecoveryOpen(false)
    setIssuedRecoveryCodes([])
    setSecretForm(emptySecretForm)
  }

  async function regenerateRecoveryCodes() {
    if (busy) return
    if (!/^\d{6}$/.test(secretForm.currentTotp.trim())) {
      setError('Enter the current 6-digit authenticator code.')
      return
    }
    setBusy('recovery')
    setError('')
    try {
      const result = await post('/api/admin/security/recovery-codes/regenerate', {
        currentPassword: secretForm.currentPassword,
        currentTotp: secretForm.currentTotp.trim(),
      })
      if (!Array.isArray(result.recoveryCodes) || result.recoveryCodes.length !== 10) throw new Error('New recovery codes were not returned safely.')
      setIssuedRecoveryCodes(result.recoveryCodes)
      setSecretForm(emptySecretForm)
      setNotice('Recovery codes regenerated. Save the new codes before closing this dialog; the previous codes are invalid.')
      await load()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Recovery codes could not be regenerated.')
    } finally {
      setBusy('')
    }
  }

  async function logout() {
    if (!session || logoutBusy) return
    setLogoutBusy(true)
    try {
      const response = await fetch('/api/admin/logout', {
        method: 'POST', credentials: 'same-origin', headers: { 'X-CSRF-Token': session.csrfToken },
      })
      if (!response.ok) throw new Error('logout failed')
      window.location.replace('/admin/login')
    } catch {
      setError('Logout could not be completed. Please try again.')
      setLogoutBusy(false)
    }
  }

  if (!loading && error && (!session || !security)) {
    return <main className="admin-shell-gate-error"><AdminStatePanel state="error" title="Admin & Security unavailable" description={error} action={<AdminButton type="button" onClick={() => void load()}>Retry</AdminButton>} /></main>
  }
  if (!session || !security) {
    return <main className="admin-shell-gate-loading" aria-label="Loading Admin & Security"><AdminCard><AdminSkeleton lines={9} /></AdminCard></main>
  }

  const otherSessions = security.sessions.filter((item) => !item.current)

  return (
    <AdminShell
      title="Admin & Security"
      eyebrow="SECURITY"
      description="Owner access, MFA, active sessions and security activity."
      user={session.user}
      onLogout={() => void logout()}
      logoutBusy={logoutBusy}
      environmentLabel={environmentLabel}
    >
      {notice && <div className="admin-security__notice" role="status">{notice}</div>}
      {error && <div className="admin-security__error" role="alert">{error}</div>}

      <div className="admin-security__overview-grid">
        <AdminCard padding="large">
          <AdminCardHeader
            title="Your account"
            description="The currently authenticated Boss Zone owner."
            action={<AdminStatusPill tone={security.account.status === 'active' ? 'success' : 'warning'}>{security.account.status}</AdminStatusPill>}
          />
          <dl className="admin-security__facts">
            <div><dt>Email</dt><dd>{security.account.email}</dd></div>
            <div><dt>Role</dt><dd>{security.account.role}</dd></div>
            <div><dt>Last login</dt><dd>{when(security.account.lastLoginAt)}</dd></div>
            <div><dt>Account created</dt><dd>{when(security.account.createdAt)}</dd></div>
          </dl>
          <div className="admin-security__account-actions">
            <AdminButton type="button" onClick={() => { setError(''); setPasswordOpen(true) }}>Change password</AdminButton>
          </div>
        </AdminCard>

        <AdminCard padding="large">
          <AdminCardHeader
            title="MFA & recovery"
            description="Authenticator MFA remains required for owner access."
            action={<AdminStatusPill tone={security.account.mfa.enrolled ? 'success' : 'danger'}>{security.account.mfa.enrolled ? 'MFA enrolled' : 'MFA required'}</AdminStatusPill>}
          />
          <dl className="admin-security__facts">
            <div><dt>MFA enrolled</dt><dd>{security.account.mfa.enrolled ? when(security.account.mfa.enrolledAt) : 'Not enrolled'}</dd></div>
            <div><dt>Unused recovery codes</dt><dd>{security.account.recoveryCodesRemaining}</dd></div>
            <div><dt>Step-up verification</dt><dd>Current password + current TOTP</dd></div>
          </dl>
          <div className="admin-security__account-actions">
            <AdminButton variant="secondary" type="button" onClick={() => { setError(''); setRecoveryOpen(true) }}>Regenerate recovery codes</AdminButton>
          </div>
          <p className="admin-security__muted">MFA replacement is intentionally not available in V1.</p>
        </AdminCard>

        <AdminCard padding="large">
          <AdminCardHeader title="Authentication policy" description="Read-only security policy enforced by the existing Boss Zone authentication service." />
          <dl className="admin-security__facts">
            <div><dt>Access</dt><dd>Owner only</dd></div>
            <div><dt>Password length</dt><dd>{security.policy.passwordMinLength}–{security.policy.passwordMaxLength} characters</dd></div>
            <div><dt>Session expiry</dt><dd>{security.policy.sessionAbsoluteHours}h absolute / {security.policy.sessionIdleHours}h idle</dd></div>
            <div><dt>Cookies</dt><dd>{security.policy.cookieSecurity.join(' · ')}</dd></div>
            <div><dt>Protected writes</dt><dd>Same-origin + session CSRF token</dd></div>
            <div><dt>Step-up limit</dt><dd>{security.policy.stepUpFailureLimit} failures / {security.policy.stepUpWindowMinutes} min</dd></div>
          </dl>
        </AdminCard>
      </div>

      <AdminCard className="admin-security__sessions" padding="large">
        <AdminCardHeader
          title="Active sessions"
          description="Only safe session metadata is shown. Session tokens, hashes, CSRF tokens and request fingerprints never leave the server."
          action={otherSessions.length > 0 ? <AdminButton variant="danger" compact type="button" onClick={() => setRevokeOthersOpen(true)}>Revoke all other sessions</AdminButton> : undefined}
        />
        <AdminDataTable
          caption="Active owner sessions"
          rowKey={(row: SecuritySession) => row.id}
          rows={security.sessions}
          columns={[
            { key: 'session', label: 'Session', render: (row: SecuritySession) => <div className="admin-security__session-name"><strong>{row.current ? 'Current session' : 'Other session'}</strong>{row.current && <AdminStatusPill tone="success">Current</AdminStatusPill>}</div> },
            { key: 'created', label: 'Started', render: (row: SecuritySession) => when(row.createdAt) },
            { key: 'lastSeen', label: 'Last active', render: (row: SecuritySession) => when(row.lastSeenAt) },
            { key: 'expires', label: 'Expires', render: (row: SecuritySession) => when(row.absoluteExpiresAt) },
            { key: 'actions', label: '', align: 'right', render: (row: SecuritySession) => row.current ? <span className="admin-security__protected">Protected</span> : <AdminButton variant="danger" compact type="button" onClick={() => setRevokeTarget(row)}>Revoke</AdminButton> },
          ]}
        />
      </AdminCard>

      <div className="admin-security__lower-grid">
        <AdminCard padding="large">
          <AdminCardHeader title="Authorised users" description="Actual Boss Zone identities only. User creation, invitations, roles and permissions are not editable in V1." />
          <AdminDataTable
            caption="Authorised Boss Zone users"
            rowKey={(row: SecurityUser) => row.email}
            rows={security.users}
            columns={[
              { key: 'email', label: 'User', render: (row: SecurityUser) => <strong>{row.email}</strong> },
              { key: 'role', label: 'Role', render: (row: SecurityUser) => row.role },
              { key: 'status', label: 'Status', render: (row: SecurityUser) => <AdminStatusPill tone={row.status === 'active' ? 'success' : 'warning'}>{row.status}</AdminStatusPill> },
              { key: 'mfa', label: 'MFA', render: (row: SecurityUser) => row.mfaEnrolled ? 'Enrolled' : 'Not enrolled' },
              { key: 'last', label: 'Last login', render: (row: SecurityUser) => when(row.lastLoginAt) },
            ]}
          />
          <p className="admin-security__muted">The schema is future staff-capable, but staff access remains disabled until Kyle explicitly authorises a separate account and permissions workflow.</p>
        </AdminCard>

        <AdminCard padding="large">
          <AdminCardHeader title="Security activity" description="Curated owner-facing authentication and security events. Raw audit details are never sent to the browser." />
          {security.activity.length ? (
            <div className="admin-security__activity-list">
              {security.activity.map((item, index) => (
                <div className="admin-security__activity" key={`${item.createdAt}-${item.event}-${index}`}>
                  <div>
                    <strong>{item.label}</strong>
                    <span>{item.actor}</span>
                  </div>
                  <div>
                    <AdminStatusPill tone={outcomeTone(item.outcome)}>{item.outcome}</AdminStatusPill>
                    <time>{when(item.createdAt)}</time>
                  </div>
                </div>
              ))}
            </div>
          ) : <AdminStatePanel state="empty" title="No security activity yet" description="Security events will appear here as they are recorded." />}
        </AdminCard>
      </div>

      <AdminModal
        open={Boolean(revokeTarget)}
        title="Revoke this session?"
        description="This will immediately invalidate the selected other Boss Zone session. Your current session is protected."
        onClose={() => !busy && setRevokeTarget(null)}
        footer={<>
          <AdminButton variant="secondary" type="button" disabled={Boolean(busy)} onClick={() => setRevokeTarget(null)}>Cancel</AdminButton>
          <AdminButton variant="danger" type="button" disabled={Boolean(busy)} onClick={() => void revokeSession()}>{busy === 'revoke-session' ? 'Revoking…' : 'Revoke session'}</AdminButton>
        </>}
      />

      <AdminModal
        open={revokeOthersOpen}
        title="Revoke all other sessions?"
        description="Every other active owner session will be invalidated. This current session will remain active."
        onClose={() => !busy && setRevokeOthersOpen(false)}
        footer={<>
          <AdminButton variant="secondary" type="button" disabled={Boolean(busy)} onClick={() => setRevokeOthersOpen(false)}>Cancel</AdminButton>
          <AdminButton variant="danger" type="button" disabled={Boolean(busy)} onClick={() => void revokeOthers()}>{busy === 'revoke-others' ? 'Revoking…' : 'Revoke other sessions'}</AdminButton>
        </>}
      />

      <AdminModal
        open={passwordOpen}
        title="Change owner password"
        description="This high-impact action requires your current password and a fresh authenticator TOTP. Other sessions will be revoked after success."
        onClose={closePassword}
        footer={<>
          <AdminButton variant="secondary" type="button" disabled={Boolean(busy)} onClick={closePassword}>Cancel</AdminButton>
          <AdminButton type="button" disabled={Boolean(busy)} onClick={() => void changePassword()}>{busy === 'password' ? 'Changing…' : 'Confirm password change'}</AdminButton>
        </>}
      >
        <div className="admin-security__step-up-form">
          <AdminField label="Current password" required><AdminInput type="password" autoComplete="current-password" value={secretForm.currentPassword} onChange={(event) => updateSecretField('currentPassword', event.target.value)} /></AdminField>
          <AdminField label="Current authenticator code" hint="Recovery codes cannot authorise this action." required><AdminInput inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={secretForm.currentTotp} onChange={(event) => updateSecretField('currentTotp', event.target.value.replace(/\D/g, '').slice(0, 6))} /></AdminField>
          <AdminField label="New password" hint={`${security.policy.passwordMinLength}–${security.policy.passwordMaxLength} characters.`} required><AdminInput type="password" autoComplete="new-password" value={secretForm.newPassword} onChange={(event) => updateSecretField('newPassword', event.target.value)} /></AdminField>
          <AdminField label="Confirm new password" required><AdminInput type="password" autoComplete="new-password" value={secretForm.confirmPassword} onChange={(event) => updateSecretField('confirmPassword', event.target.value)} /></AdminField>
        </div>
      </AdminModal>

      <AdminModal
        open={recoveryOpen}
        title={issuedRecoveryCodes.length ? 'Save your new recovery codes' : 'Regenerate recovery codes'}
        description={issuedRecoveryCodes.length ? 'These ten codes are shown once. Store them somewhere secure before closing this dialog.' : 'This invalidates every existing recovery code. Current password and authenticator TOTP are required.'}
        onClose={closeRecovery}
        footer={issuedRecoveryCodes.length ? (
          <AdminButton type="button" onClick={closeRecovery}>I have saved these codes</AdminButton>
        ) : <>
          <AdminButton variant="secondary" type="button" disabled={Boolean(busy)} onClick={closeRecovery}>Cancel</AdminButton>
          <AdminButton variant="danger" type="button" disabled={Boolean(busy)} onClick={() => void regenerateRecoveryCodes()}>{busy === 'recovery' ? 'Regenerating…' : 'Regenerate codes'}</AdminButton>
        </>}
      >
        {issuedRecoveryCodes.length ? (
          <ol className="admin-security__recovery-codes">
            {issuedRecoveryCodes.map((code) => <li key={code}><code>{code}</code></li>)}
          </ol>
        ) : (
          <div className="admin-security__step-up-form">
            <AdminField label="Current password" required><AdminInput type="password" autoComplete="current-password" value={secretForm.currentPassword} onChange={(event) => updateSecretField('currentPassword', event.target.value)} /></AdminField>
            <AdminField label="Current authenticator code" hint="A recovery code is not accepted as step-up verification." required><AdminInput inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={secretForm.currentTotp} onChange={(event) => updateSecretField('currentTotp', event.target.value.replace(/\D/g, '').slice(0, 6))} /></AdminField>
          </div>
        )}
      </AdminModal>
    </AdminShell>
  )
}
